import type { ReactNode } from "react"
import dynamic from "next/dynamic"
import Link from "next/link"

import { Button, Card } from "@coval/ui"

import { formatCompactNumber } from "@/utils/formatting/format-number"

const HomeStatCardSparkline = dynamic(
  () => import("./HomeStatCardSparkline").then((m) => m.HomeStatCardSparkline),
  { ssr: false },
)

interface SparklinePoint {
  date: string
  count: number
  extrapolated?: boolean
}

interface HomeStatCardProps {
  title: string
  count: number
  isLoading?: boolean
  href: string
  variant?: "grouped" | "discrete"
  /** When true, do not render card border (section provides border/divide) to avoid double borders */
  suppressBorder?: boolean
  /** Optional suffix for value (e.g. "%" for agreement rate) */
  suffix?: string
  /** When set, show this instead of formatCount(count); use for placeholders like "—" or deferred values */
  countDisplay?: string | null
  /** Optional daily data for sparkline (omit for discrete/config stats) */
  sparklineData?: SparklinePoint[]
  /** Optional "+" button rendered right-aligned */
  newButtonHref?: string
  newButtonLabel?: string
  /** CTA label shown when count is 0 and newButtonHref is set (e.g. "Create your first agent") */
  emptyCtaLabel?: string
  /** Vertical alignment of card content (default: center) */
  contentAlign?: "top" | "center"
}

const SPARKLINE_HEIGHT = 28

export function HomeStatCard({
  title,
  count,
  isLoading = false,
  href,
  variant = "grouped",
  suppressBorder = false,
  suffix,
  countDisplay,
  sparklineData,
  newButtonHref,
  newButtonLabel,
  emptyCtaLabel,
  contentAlign = "center",
}: HomeStatCardProps) {
  const isDiscrete = variant === "discrete" && !suppressBorder
  const displayValue =
    countDisplay != null
      ? countDisplay
      : suffix != null
        ? `${formatCompactNumber(count)}${suffix}`
        : formatCompactNumber(count)
  // A single point can't render a line/area; recharts force-draws it as a lone
  // dot, so require at least two points before showing the sparkline.
  const showSparkline =
    !isDiscrete &&
    sparklineData != null &&
    sparklineData.length >= 2 &&
    !isLoading

  const hasNewButton =
    newButtonHref != null && newButtonLabel != null && !showSparkline
  const showEmptyCta =
    hasNewButton && count === 0 && !isLoading && emptyCtaLabel != null

  const titleAndValue = (
    <div className="flex flex-col items-start gap-0.5">
      <span className="text-sm font-normal text-coval-text-muted">{title}</span>
      <p className="text-2xl font-bold tabular-nums text-coval-text leading-tight">
        {isLoading ? (
          <span className="inline-block w-12 h-[1em] bg-surface-default-hover rounded animate-pulse align-middle" />
        ) : (
          displayValue
        )}
      </p>
    </div>
  )

  let body: ReactNode
  if (showEmptyCta && newButtonHref) {
    body = (
      <div className="h-full px-4 py-2.5 flex items-center justify-center">
        <Button asChild variant="secondary" className="text-center">
          <Link href={newButtonHref}>{emptyCtaLabel}</Link>
        </Button>
      </div>
    )
  } else if (hasNewButton && newButtonHref) {
    body = (
      <div className="h-full flex flex-row items-stretch justify-between gap-0.5">
        <Link
          href={href}
          className="flex-1 flex flex-col items-start justify-center gap-0.5 px-4 py-2.5 text-left"
        >
          {titleAndValue}
        </Link>
        <div className="flex items-center pr-4 py-2.5">
          <Button
            asChild
            variant="secondary"
            icon
            tooltip={`Create new ${title.toLowerCase()}`}
            className="shrink-0"
          >
            <Link href={newButtonHref}>{newButtonLabel}</Link>
          </Button>
        </div>
      </div>
    )
  } else {
    // The link stretches over the whole card, and the sparkline sits above it,
    // so the sparkline's table toggle is a control of its own, not one nested
    // in the link.
    body = (
      <div
        className={`relative h-full px-4 py-2.5 flex flex-col items-start gap-0.5 text-left ${contentAlign === "top" ? "justify-start" : ""}`}
      >
        <Link
          href={href}
          className="after:absolute after:inset-0 after:content-['']"
        >
          {titleAndValue}
        </Link>
        <div
          className="relative z-10 w-full mt-1 shrink-0 flex items-center"
          style={{
            height: SPARKLINE_HEIGHT,
            minHeight: SPARKLINE_HEIGHT,
          }}
        >
          {showSparkline && (
            <HomeStatCardSparkline
              title={title}
              sparklineData={sparklineData}
              height={SPARKLINE_HEIGHT}
            />
          )}
        </div>
      </div>
    )
  }

  if (isDiscrete) {
    return (
      <Card className="h-full rounded-sm shadow-none">
        <div className="h-full">{body}</div>
      </Card>
    )
  }

  return <div className="h-full bg-surface-default">{body}</div>
}
